import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, ElementRef, HostListener, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Keys } from '@skylab-shared/ui';

import { ButtonComponent } from '../button/button.component';
import { FocusByDirective } from '../focus-by/focus-by.directive';
import { DialogHeaderComponentIns } from './dialog-header-ins.component';
import { DialogInsService } from './dialog-ins.service';
import { DialogButton, DialogButtonResult, DialogState } from './dialog.model';

@Component({
    selector: 'skl-confirmation-dialog-ins',
    template: `
        <skl-dialog-header-ins
            id="confirmation-dialog-header"
            [title]="title()"
            (onClose)="onCancelAsync(null)">
        </skl-dialog-header-ins>

        <ins-content id="confirmation-dialog-content">
            <div class="text--pre-line align--small">{{ text() }}</div>
        </ins-content>

        <ins-footer class="dialog__buttons" [skl-focus-by]="'.js-init-focus'">
            @for (btn of displayButtons(); track btn; let idx = $index) {
                <skl-button
                    [id]="'confirmation-dialog-button-' + idx"
                    [typeName]="btn.type"
                    [class]="btn.focus ? 'js-init-focus' : ''"
                    (click)="onActionAsync(btn)">
                    {{ btn.text }}
                </skl-button>
            }
        </ins-footer>
    `,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogHeaderComponentIns, FocusByDirective, ButtonComponent]
})
export class ConfirmationDialogInsComponent implements OnInit {
    readonly title = input<string>('');
    readonly text = input<string>('');
    readonly buttons = input<DialogButton[]>([]);
    readonly displayButtons = computed(() => {
        const orderMap: Record<DialogState, number> = {
            [DialogState.Cancel]: 0,
            [DialogState.Decline]: 1,
            [DialogState.Confirm]: 2,
        };
        const list = [...this.buttons()];
        return list.sort((a, b) => (orderMap[a.state] ?? 0) - (orderMap[b.state] ?? 0));
    });

    constructor(
        protected readonly elementRef: ElementRef = inject(ElementRef),
        protected readonly dialogInsService: DialogInsService = inject(DialogInsService)
    ) {
        this.dialogInsService.keyAction$
            .pipe(takeUntilDestroyed())
            .subscribe(state => {
                this.onActionAsync({ state });
            });
    }

    @HostListener('keydown', ['$event'])
    protected onKeyDown(e: KeyboardEvent): void {
        const activeEl = document.activeElement;
        if (!activeEl) return;

        if (e.key === 'ArrowLeft' || e.keyCode === Keys.Left) {
            const currentSklButton = activeEl.closest('skl-button');
            currentSklButton?.nextElementSibling?.querySelector<HTMLButtonElement>('button')?.focus();
        } else if (e.key === 'ArrowRight' || e.keyCode === Keys.Right) {
            const currentSklButton = activeEl.closest('skl-button');
            currentSklButton?.previousElementSibling?.querySelector<HTMLButtonElement>('button')?.focus();
        }
    }

    ngOnInit() {}

    async onActionAsync(data: DialogButtonResult): Promise<void> {
        this.dialogInsService.close(data);
    }

    async onCancelAsync(_: DialogButtonResult | null): Promise<void> {
        this.dialogInsService.close({ value: null, state: DialogState.Cancel });
    }
}
